// @flow
import * as React from 'react';
import { Position } from '@locus-taxy/neo-ui';

import variables from 'common/components/core/variables.scss';
import { type ContextData } from 'platform/settings/form/renderers/util/ctxToSettingsContextData';

import ModalHeader from './ModalHeader';
import ModalContent from './ModalContent';

import ObjectPropertiesEditDrawer from './ObjectPropertiesEditDrawer';
import { getInitalFormValue } from './utils';

import type { ObjectArray, Labels, ObjectArrayMetadata } from './types';

type Props = {
    canDelete?: boolean,
    context: ContextData,
    i18n: $FixMe,
    labels: Labels,
    metadata: ObjectArrayMetadata,
    objectArray: ObjectArray,
    onDismiss: () => void,
    onSave: (data: ObjectArray) => void
};

const FormMode = {
    add: 'add',
    edit: 'edit',
    hide: 'hide'
};

type Mode = $Values<typeof FormMode>;

const ArrayObjectModal = ({
    canDelete,
    context,
    i18n,
    labels,
    metadata,
    objectArray,
    onDismiss,
    onSave
}: Props): React.Node => {
    const [formMode, setFormMode] = React.useState<Mode>(FormMode.hide);
    const [fieldToEdit, setFieldToEdit] = React.useState();

    const { edit, view } = metadata;

    const value = React.useMemo(() => {
        switch (formMode) {
            case FormMode.add:
                return { ...getInitalFormValue(view), context };

            case FormMode.edit:
                return { item: fieldToEdit, context };

            default:
                return undefined;
        }
    }, [context, fieldToEdit, formMode, view]);

    const dismissModal = () => {
        setFieldToEdit(undefined);
        setFormMode(FormMode.hide);
    };

    const handleShowAddModal = () => {
        setFieldToEdit(undefined);
        setFormMode(FormMode.add);
    };

    const handleShowEditModal = data => {
        setFieldToEdit(data);
        setFormMode(FormMode.edit);
    };

    const handleDelete = () => {
        const idxToDelete = objectArray.findIndex(
            field => field === fieldToEdit
        );

        onSave(objectArray.filter((_, idx) => idx !== idxToDelete));
        dismissModal();
    };

    const handleSave = data => {
        if (formMode === FormMode.add) {
            onSave((objectArray || []).concat(data));
        } else {
            const updatedArray = [...objectArray];
            const idx = objectArray.findIndex(field => field === fieldToEdit);
            updatedArray[idx] = data;

            onSave(updatedArray);
        }

        dismissModal();
    };

    return (
        <>
            <Position
                position="fixed"
                backgroundColor="grey.100"
                top={variables.appbarHeight}
                left={variables.leftbarWidth}
                right={0}
                bottom={0}
                zIndex={15}
                overflow="auto"
            >
                <ModalHeader
                    labels={labels}
                    onDismiss={onDismiss}
                    onAdd={handleShowAddModal}
                    totalNoOfFields={objectArray.length}
                />

                <ModalContent
                    objectArray={objectArray}
                    i18n={i18n}
                    metadata={metadata}
                    onEdit={handleShowEditModal}
                />
            </Position>

            {value && formMode !== FormMode.hide && (
                <ObjectPropertiesEditDrawer
                    editMetadata={FormMode.edit ? edit : view}
                    i18n={i18n}
                    labels={labels.drawer}
                    onDismiss={dismissModal}
                    onDelete={
                        canDelete && formMode !== FormMode.add
                            ? handleDelete
                            : undefined
                    }
                    onSave={handleSave}
                    value={value}
                />
            )}
        </>
    );
};

export default ArrayObjectModal;
